import { ChartConfig, ChartSeries, ChartType } from "@/components/ChartRenderer";
import type { ChartDataPoint } from "@/components/ChartRenderer";

interface ParsedChartSpec {
  config: ChartConfig;
  rawSpec: any;
}

const DEFAULT_CHART_TYPE: ChartType = "line";
// Keep identifiers for potential future heuristics, but we no longer rely on them
// for auto-detection from plain paragraphs to reduce unwanted charts.
const CHART_IDENTIFIERS = [
  "chart",
  "visualization",
];

const SERIES_TYPE_MAPPING: Record<string, ChartType> = {
  line: "line",
  lines: "line",
  "line chart": "line",
  "multi-line": "line",
  "area": "area",
  "stacked area": "stacked-area",
  bar: "bar",
  bars: "bar",
  "grouped bar": "bar",
  "stacked bar": "bar",
  "mono bar": "monochrome-bar",
  column: "bar",
  columns: "bar",
  histogram: "bar",
  pie: "pie",
  donut: "donut",
  doughnut: "donut",
  "pie chart": "pie",
  "donut chart": "donut",
  "doughnut chart": "donut",
  scatter: "scatter",
  "scatter plot": "scatter",
  bubble: "bubble",
  "bubble chart": "bubble",
  radar: "radar",
  "radar chart": "radar",
  spider: "radar",
  "spider chart": "radar",
  mixed: "mixed",
  combo: "mixed",
  combination: "mixed",
  "dual axis": "mixed",
};

const CHART_LANGUAGES = new Set([
  "chart",
  "chart-json",
  "chart-spec",
  "visualization",
  "viz",
  "chartjs",
]);

const normalizeKey = (key: string) =>
  key
    .trim()
    .replace(/[^a-zA-Z0-9_\- ]/g, "")
    .replace(/\s+/g, " ");

const mapChartTypeToSeriesType = (chartType?: ChartType): ChartSeries["type"] => {
  if (!chartType) return undefined;
  switch (chartType) {
    case "line":
    case "mixed":
      return "line";
    case "area":
    case "stacked-area":
      return "area";
    case "bar":
    case "monochrome-bar":
      return "bar";
    default:
      return undefined;
  }
};

const inferChartType = (spec: any): ChartType => {
  if (!spec) return DEFAULT_CHART_TYPE;

  if (typeof spec.type === "string") {
    const normalizedType = spec.type.toLowerCase();
    if (SERIES_TYPE_MAPPING[normalizedType]) {
      return SERIES_TYPE_MAPPING[normalizedType];
    }
  }

  if (spec.series && Array.isArray(spec.series)) {
    const uniqueTypes = new Set<ChartType>();
    for (const series of spec.series) {
      if (typeof series.type === "string") {
        const normalizedType = series.type.toLowerCase();
        if (SERIES_TYPE_MAPPING[normalizedType]) {
          uniqueTypes.add(SERIES_TYPE_MAPPING[normalizedType]);
        }
      }
    }
    if (uniqueTypes.size === 1) {
      return Array.from(uniqueTypes)[0] as ChartType;
    }
    if (uniqueTypes.size > 1) {
      return "mixed";
    }
  }

  if (spec.data && Array.isArray(spec.data)) {
    const firstRow = spec.data[0];
    const numericValues = Object.values(firstRow || {}).filter((value) => typeof value === "number");
    if (numericValues.length >= 2) {
      return "mixed";
    }
  }

  return DEFAULT_CHART_TYPE;
};

const sanitizeData = (data: any[]): ChartDataPoint[] => {
  if (!Array.isArray(data)) return [];

  return data.map((row, index) => {
    if (typeof row === "object" && row !== null) {
      return Object.entries(row).reduce<ChartDataPoint>((acc, [key, value]) => {
        const normalizedKey = normalizeKey(key);
        if (value === null || value === undefined || value === "") {
          acc[normalizedKey] = null;
          return acc;
        }
        if (typeof value === "number") {
          acc[normalizedKey] = value;
          return acc;
        }
        if (typeof value === "string") {
          const numericValue = Number(value);
          acc[normalizedKey] = Number.isFinite(numericValue) ? numericValue : value;
          return acc;
        }
        if (typeof value === "boolean") {
          acc[normalizedKey] = value ? 1 : 0;
          return acc;
        }
        acc[normalizedKey] = String(value);
        return acc;
      }, {});
    }

    return { index, value: row };
  });
};

const sanitizeSeries = (series: any[]): ChartSeries[] => {
  if (!Array.isArray(series)) return [];

  return series
    .map((serie) => {
      if (!serie || typeof serie !== "object") return null;

      const key = typeof serie.key === "string" ? normalizeKey(serie.key) : null;
      const name = typeof serie.name === "string" ? serie.name.trim() : undefined;
      const chartType =
        typeof serie.type === "string"
          ? SERIES_TYPE_MAPPING[serie.type.toLowerCase()] || undefined
          : undefined;
      const type = mapChartTypeToSeriesType(chartType);
      const color = typeof serie.color === "string" ? serie.color.trim() : undefined;
      const strokeWidth = typeof serie.strokeWidth === "number" ? serie.strokeWidth : undefined;
      const yAxisId = typeof serie.yAxisId === "string" ? normalizeKey(serie.yAxisId) : undefined;

      if (!key) return null;

      return {
        key,
        name,
        type,
        color,
        strokeWidth,
        yAxisId,
      } satisfies ChartSeries;
    })
    .filter(Boolean) as ChartSeries[];
};

const detectChartSpec = (text: string): any | null => {
  if (!text || typeof text !== "string") return null;

  // Find fenced code blocks with language
  const blockRegex = /```([a-zA-Z0-9_-]*)\n([\s\S]*?)```/g;
  let match: RegExpExecArray | null;
  while ((match = blockRegex.exec(text)) !== null) {
    const lang = (match[1] || "").toLowerCase();
    const innerContent = match[2] || "";

    // Only consider explicit chart languages, or JSON with explicit chart intent flag
    const isExplicitChartLang = CHART_LANGUAGES.has(lang);
    const hasExplicitChartFlag = /\b("chart"|"visualization")\s*:\s*(true|"true")/i.test(innerContent);
    if (!(isExplicitChartLang || (lang === "json" && hasExplicitChartFlag))) {
      continue;
    }

    try {
      const parsed = JSON.parse(innerContent);
      if (
        parsed &&
        (
          parsed.chart === true ||
          parsed.visualization === true ||
          parsed.type !== undefined ||
          parsed.series !== undefined ||
          parsed.data !== undefined
        )
      ) {
        return parsed;
      }
    } catch {
      // ignore malformed JSON, continue searching
    }
  }
  return null;
};

export const parseChartConfig = (text: string): ParsedChartSpec | null => {
  let spec: any = null;

  if (!text) {
    return null;
  }

  // Only accept direct JSON if it explicitly indicates intent to render a chart
  try {
    const direct = JSON.parse(text);
    if (
      direct &&
      typeof direct === "object" &&
      (direct.chart === true || direct.visualization === true)
    ) {
      spec = direct;
    }
  } catch {
    // Not plain JSON; proceed to fenced block detection
  }

  if (!spec) {
    spec = detectChartSpec(text);
  }

  if (!spec) return null;

  const type = inferChartType(spec);
  const data = sanitizeData(spec.data || []);
  const xKey = typeof spec.xKey === "string" ? normalizeKey(spec.xKey) : spec.categoryKey ? normalizeKey(spec.categoryKey) : Object.keys(data[0] || {})[0];

  const yKeys = sanitizeSeries(spec.series || spec.yKeys || []);

  if (yKeys.length === 0 && data.length > 0) {
    const potentialKeys = Object.keys(data[0]).filter((key) => key !== xKey);
    potentialKeys.forEach((key) => {
      yKeys.push({ key, name: key });
    });
  }

  if (!xKey || yKeys.length === 0) return null;

  const config: ChartConfig = {
    type,
    title: typeof spec.title === "string" ? spec.title.trim() : undefined,
    description: typeof spec.description === "string" ? spec.description.trim() : undefined,
    xKey,
    yKeys,
    data,
    colors: Array.isArray(spec.colors) ? spec.colors.filter((color: any) => typeof color === "string") : undefined,
    valueFormatter: undefined,
    categoryKey: spec.categoryKey,
    unit: typeof spec.unit === "string" ? spec.unit : undefined,
    showLegend: typeof spec.showLegend === "boolean" ? spec.showLegend : undefined,
    showTooltip: typeof spec.showTooltip === "boolean" ? spec.showTooltip : undefined,
    stacked: typeof spec.stacked === "boolean" ? spec.stacked : undefined,
    area: typeof spec.area === "boolean" ? spec.area : undefined,
    scatterSizeKey: typeof spec.scatterSizeKey === "string" ? spec.scatterSizeKey : undefined,
    radarAngleKey: typeof spec.angleKey === "string" ? spec.angleKey : undefined,
    radarRadiusKey: typeof spec.radiusKey === "string" ? spec.radiusKey : undefined,
    mixedConfig: Array.isArray(spec.mixedConfig) ? sanitizeSeries(spec.mixedConfig) : undefined,
    // Axis labels from various possible fields
    xLabel:
      typeof spec.xLabel === "string"
        ? spec.xLabel
        : typeof spec.xAxisLabel === "string"
        ? spec.xAxisLabel
        : spec.axes && spec.axes.x && typeof spec.axes.x.label === "string"
        ? spec.axes.x.label
        : undefined,
    yLabel:
      typeof spec.yLabel === "string"
        ? spec.yLabel
        : typeof spec.yAxisLabel === "string"
        ? spec.yAxisLabel
        : spec.axes && spec.axes.y && typeof spec.axes.y.label === "string"
        ? spec.axes.y.label
        : undefined,
  };

  return { config, rawSpec: spec };
};

export const extractFirstChartConfig = (text: string): ChartConfig | null => {
  const parsed = parseChartConfig(text);
  return parsed ? parsed.config : null;
};

export const removeChartSpecsFromContent = (text: string): string => {
  if (!text) return text;
  return text.replace(/```(?:json|chart|viz|visualization|chart-spec)?\n([\s\S]*?)```/gi, "").trim();
};

export const isChartCodeBlock = (language?: string) => {
  if (!language) return false;
  return CHART_LANGUAGES.has(language.toLowerCase());
};
